import React, { forwardRef } from "react";
import {
  ActionIcon,
  Avatar,
  Group,
  Menu,
  Select,
  Tabs,
  Text,
  useMantineColorScheme,
  useMantineTheme,
} from "@mantine/core";
import { useGlobalStatusContext } from "../GlobalStore";
import { useNavigate } from "./Link";

import queryString from "query-string";
import { Bsc, Eth } from "./EpIcon";
import { useIsSmallerThanMD, useIsSmallScreen } from "./Utils";
import { resetTokenCache } from "./TokenDisplay";

export const ChainSwitch = React.memo(function ({
  type = "select",
  isCollapse = false,
}: {
  type?: "select" | "tab";
  isCollapse?: boolean;
}) {
  const { chain, setChain } = useGlobalStatusContext();
  const navigate = useNavigate();
  const isSmallScreen = useIsSmallScreen();
  const isSmallerThanMD = useIsSmallerThanMD();
  const qs: any = queryString.parse(window.location.search);

  const { colorScheme } = useMantineColorScheme();
  const theme = useMantineTheme();
  const dark = colorScheme === "dark";

  interface ItemProps extends React.ComponentPropsWithoutRef<"div"> {
    image: string;
    label: string;
    value?: string;
  }

  const SelectItem = forwardRef<HTMLDivElement, ItemProps>(
    ({ image, label, ...others }: ItemProps, ref) => (
      <div
        ref={ref}
        {...others}
        id={`chainswitch-${others?.value}`}
        onClick={() => onChange(others?.value || "")}
      >
        <Group noWrap>
          {isSmallScreen ? null : <Avatar src={image} size="xs" />}
          <div>
            <Text size={isSmallScreen ? "xs" : "sm"}>{label}</Text>
          </div>
        </Group>
      </div>
    )
  );

  const onChange = (value: string) => {
    resetTokenCache();

    if (qs["chain"] || window.location.pathname.match(/bsc|ethereum/)) {
      if (qs["chain"]) {
        qs["chain"] = value;
      }
      const pathname = window.location.pathname.replace(/bsc|ethereum/, value);
      navigate(`${pathname}?${queryString.stringify(qs)}`);
    } else {
      setChain(value);
    }
  };

  if (type === "tab") {
    return (
      <Tabs
        id="eigenphi-chain-switch"
        variant="pills"
        value={chain}
        styles={{
          tab: {
            padding: isSmallScreen
              ? "5px 7px 5px 4px !important"
              : "8px 11px !important",
            color: dark ? "#FFF !important" : "#000 !important",
            border: "1px solid transparent",
            boxSizing: "border-box",
            "&[data-active=true]": {
              color: dark ? "#FFF !important" : "#000 !important",
              borderColor: dark ? "#373A40" : "#CED4DA",
              backgroundColor: "transparent !important",
            },
            "&:hover": isSmallScreen
              ? {
                  color: dark ? "#FFF !important" : "#000 !important",
                  borderColor: dark ? "#373A40" : "#CED4DA",
                  backgroundColor: "transparent !important",
                }
              : {
                  color: "#44D7B6 !important",
                  borderColor: "rgba(68,215,182,0.50)",
                  backgroundColor: "rgba(68,215,182,0.10) !important",
                },
          },
          tabIcon: {
            marginRight: "6px !important",
          },
          tabLabel: {
            fontWeight: "normal",
          },
          tabsList: {
            flexWrap: "nowrap",
          },
        }}
        onTabChange={(value) => {
          onChange(value!);
        }}
      >
        <Tabs.List>
          <Tabs.Tab value="ethereum" icon={<Eth size={18} />}>
            {isSmallerThanMD && chain === "bsc"
              ? ""
              : isSmallerThanMD
              ? "ETH"
              : "Ethereum"}
          </Tabs.Tab>
          {/* <Tabs.Tab value="bsc" icon={<Bsc size={18} />}>
            {isSmallerThanMD && chain === "ethereum" ? "" : "BSC"}
          </Tabs.Tab> */}
        </Tabs.List>
      </Tabs>
    );
  }

  return isCollapse ? (
    <Menu position="right">
      <Menu.Target>
        <ActionIcon
          id="chainswitch"
          sx={{
            width: 50,
            height: 50,
            border: 1,
            borderColor: dark ? theme.colors.dark[4] : theme.colors.gray[4],
            borderStyle: "solid",
            backgroundColor: dark ? theme.colors.dark[4] : theme.colors.gray[1],
          }}
        >
          {chain === "ethereum" ? <Eth size={20} /> : <Bsc size={20} />}
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item
          icon={<Eth />}
          onClick={() => {
            onChange("ethereum");
          }}
        >
          Ethereum
        </Menu.Item>
        <Menu.Item
          icon={<Bsc />}
          onClick={() => {
            onChange("bsc");
          }}
        >
          BSC
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  ) : (
    <Select
      id="chainswitch"
      sx={(theme) => ({
        width: isSmallScreen ? 60 : 240,
        // backgroundColor: theme.colors.gray[8],
        // backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[8] : theme.colors.gray[0],
      })}
      styles={{
        input: {
          backgroundColor: dark ? "#141517" : "#F8F9FA",
          height: isSmallScreen ? 20 : 40,
        },
      }}
      data={[
        {
          image: "/images/chains/ethereum.svg",
          label: isSmallScreen ? "ETH" : "Ethereum",
          value: "ethereum",
        },
        {
          image: "/images/chains/bsc.svg",
          label: "BSC",
          value: "bsc",
        },
      ]}
      itemComponent={SelectItem}
      value={chain}
      onChange={onChange}
      size={isSmallScreen ? "xs" : "sm"}
      icon={
        isSmallScreen ? null : (
          <Avatar src={`/images/chains/${chain}.svg`} size="xs" />
        )
      }
    ></Select>
  );
});
